import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  FileText, 
  Receipt, 
  Clock,
  Bot,
  ArrowUpRight,
  ArrowDownRight,
  Brain,
  Sparkles,
  AlertTriangle,
  CheckCircle,
  Info,
  RefreshCw,
  LineChart as LineChartIcon,
  Shield
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import TrialReminder from '@/components/finance/TrialReminder';
import { useNavigate } from 'react-router-dom';
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend
} from 'recharts';
import { toast } from 'sonner';
import financeApiClient from '@/api/financeClient';
import { getCurrentUserWithPermissions, getRoleBadge } from '@/utils/rolePermissions';

const COLORS = ['#6366F1', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'];

const StatCard = ({ title, value, change, changeType, icon: Icon, delay }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.2 }}
  >
    <Card className="bg-orbit-card border-orbit-border hover:border-primary/30 transition-colors">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-muted-foreground mb-1">{title}</p>
            <p className="text-2xl font-heading font-bold text-white tabular-nums">{value}</p>
            {change && (
              <div className={`flex items-center gap-1 mt-2 text-sm ${changeType === 'positive' ? 'text-emerald-500' : 'text-rose-500'}`}>
                {changeType === 'positive' ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                <span>{change}</span>
              </div>
            )}
          </div>
          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
            <Icon className="w-6 h-6 text-primary" />
          </div>
        </div>
      </CardContent>
    </Card>
  </motion.div>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [insightsLoading, setInsightsLoading] = useState(true);
  const [forecast, setForecast] = useState(null);
  const [forecastLoading, setForecastLoading] = useState(true);
  
  // Get user permissions
  const user = getCurrentUserWithPermissions();
  const permissions = user?.financePermissions || {};
  const roleBadge = getRoleBadge(user?.finance_role || user?.role, 'finance');

  useEffect(() => {
    // Load dashboard stats immediately
    fetchDashboardStats();
    // Load AI insights in background (slower due to AI call)
    fetchAIInsights();
    // Load cash flow forecast
    fetchCashFlowForecast();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const response = await financeApiClient.get('/reports/dashboard');
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAIInsights = async () => {
    try {
      const response = await financeApiClient.get('/ai/insights');
      setInsights(response.data.insights || []);
    } catch (error) {
      // Silently fail - AI insights are optional
      console.log('AI insights unavailable');
    } finally {
      setInsightsLoading(false);
    }
  };

  const fetchCashFlowForecast = async () => {
    try {
      const response = await financeApiClient.get('/ai/cash-flow-forecast?months=3');
      setForecast(response.data);
    } catch (error) {
      console.log('Cash flow forecast unavailable');
    } finally {
      setForecastLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  // Prepare chart data
  const revenueData = stats?.monthly_revenue ? 
    Object.entries(stats.monthly_revenue).map(([month, value]) => ({
      month: month.slice(5),
      revenue: value,
      expenses: stats.monthly_expenses?.[month] || 0
    })).slice(-6) : [];

  const expenseData = stats?.expense_by_category ?
    Object.entries(stats.expense_by_category).map(([name, value]) => ({
      name: name.charAt(0).toUpperCase() + name.slice(1),
      value
    })) : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-page">
        <div className="animate-pulse text-primary">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="font-heading font-bold text-2xl sm:text-3xl text-white">Dashboard</h1>
            <Badge className={`${roleBadge.color} text-white text-xs`}>
              <Shield className="w-3 h-3 mr-1" />
              {roleBadge.label}
            </Badge>
          </div>
          <p className="text-muted-foreground">Welcome to MyFinance Orbit</p>
        </div>
        <div className="flex gap-3">
          <Button 
            variant="outline" 
            className="border-orbit-border"
            onClick={() => navigate('/suite/finance/ai-assistant')}
          >
            <Bot className="w-4 h-4 mr-2" />
            AI Assistant
          </Button>
          {permissions.canCreateInvoice && (
            <Button 
              className="bg-orbit-glow hover:opacity-90"
              onClick={() => navigate('/suite/finance/invoices/new')}
              data-testid="create-invoice-btn"
            >
              <FileText className="w-4 h-4 mr-2" />
              New Invoice
            </Button>
          )}
        </div>
      </div>

      {/* Trial Reminder Banner */}
      <TrialReminder />

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Revenue"
          value={formatCurrency(stats?.total_revenue)}
          change="+12.5%"
          changeType="positive"
          icon={DollarSign}
        />
        <StatCard
          title="Total Expenses"
          value={formatCurrency(stats?.total_expenses)}
          change="+8.2%"
          changeType="negative"
          icon={Receipt}
        />
        <StatCard
          title="Net Profit"
          value={formatCurrency(stats?.net_profit)}
          change={stats?.net_profit >= 0 ? '+15.3%' : '-5.2%'}
          changeType={stats?.net_profit >= 0 ? 'positive' : 'negative'}
          icon={stats?.net_profit >= 0 ? TrendingUp : TrendingDown}
        />
        <StatCard
          title="Pending Invoices"
          value={stats?.pending_invoices || 0}
          icon={Clock}
        />
      </div>

      {/* Charts Row */}
      <div className="grid lg:grid-cols-12 gap-6">
        {/* Revenue Chart */}
        <div className="lg:col-span-8">
          <Card className="bg-orbit-card border-orbit-border">
            <CardHeader>
              <CardTitle className="text-white">Revenue vs Expenses</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[300px] min-h-[300px]">
                {revenueData && revenueData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={revenueData}>
                    <defs>
                      <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#6366F1" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#6366F1" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="colorExpenses" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis dataKey="month" stroke="#64748b" fontSize={12} />
                    <YAxis stroke="#64748b" fontSize={12} tickFormatter={(v) => `${v/1000}k`} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#161822', 
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '8px'
                      }}
                      formatter={(value) => [formatCurrency(value), '']}
                    />
                    <Area type="monotone" dataKey="revenue" stroke="#6366F1" fillOpacity={1} fill="url(#colorRevenue)" name="Revenue" />
                    <Area type="monotone" dataKey="expenses" stroke="#10B981" fillOpacity={1} fill="url(#colorExpenses)" name="Expenses" />
                  </AreaChart>
                </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-slate-500">No data available</div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Expense Breakdown */}
        <div className="lg:col-span-4">
          <Card className="bg-orbit-card border-orbit-border h-full">
            <CardHeader>
              <CardTitle className="text-white">Expense Breakdown</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[200px] min-h-[200px]">
                {expenseData && expenseData.length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={expenseData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {expenseData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#161822', 
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '8px'
                      }}
                      formatter={(value) => [formatCurrency(value), '']}
                    />
                  </PieChart>
                </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-slate-500">No data</div>
                )}
              </div>
              <div className="space-y-2 mt-4">
                {expenseData.slice(0, 4).map((item, index) => (
                  <div key={item.name} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[index] }} />
                      <span className="text-muted-foreground">{item.name}</span>
                    </div>
                    <span className="text-white font-mono">{formatCurrency(item.value)}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* AI Insights & Recent Transactions */}
      <div className="grid lg:grid-cols-12 gap-6">
        {/* AI Insights */}
        <div className="lg:col-span-5">
          <Card className="bg-orbit-card border-orbit-border gradient-border">
            <CardHeader className="flex flex-row items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-orbit-glow flex items-center justify-center">
                <Bot className="w-5 h-5 text-white" />
              </div>
              <CardTitle className="text-white">AI Insights</CardTitle>
            </CardHeader>
            <CardContent>
              {insightsLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  <span>Generating AI insights...</span>
                </div>
              ) : insights.length > 0 ? (
                <ul className="space-y-3">
                  {insights.slice(0, 5).map((insight, index) => (
                    <li key={index} className="flex items-start gap-3 text-sm">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                      <span className="text-muted-foreground">{insight}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-muted-foreground text-sm">
                  Add more financial data to receive AI-powered insights about your business.
                </p>
              )}
              <Button 
                variant="ghost" 
                className="w-full mt-4 text-primary hover:text-primary"
                onClick={() => navigate('/suite/finance/ai-assistant')}
              >
                Ask AI Assistant
                <ArrowUpRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Recent Transactions */}
        <div className="lg:col-span-7">
          <Card className="bg-orbit-card border-orbit-border">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-white">Recent Transactions</CardTitle>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate('/suite/finance/transactions')}
              >
                View All
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {(stats?.recent_transactions || []).slice(0, 5).map((transaction, index) => (
                  <div key={transaction.id || index} className="flex items-center justify-between py-2 border-b border-orbit-border last:border-0">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${transaction.type === 'income' ? 'bg-emerald-500/10' : 'bg-rose-500/10'}`}>
                        {transaction.type === 'income' ? (
                          <TrendingUp className="w-5 h-5 text-emerald-500" />
                        ) : (
                          <TrendingDown className="w-5 h-5 text-rose-500" />
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-white">{transaction.description}</p>
                        <p className="text-xs text-muted-foreground">{transaction.date}</p>
                      </div>
                    </div>
                    <span className={`font-mono text-sm ${transaction.type === 'income' ? 'text-emerald-500' : 'text-rose-500'}`}>
                      {transaction.type === 'income' ? '+' : '-'}{formatCurrency(transaction.amount)}
                    </span>
                  </div>
                ))}
                {(!stats?.recent_transactions || stats.recent_transactions.length === 0) && (
                  <p className="text-center text-muted-foreground py-8">No transactions yet</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* AI Cash Flow Forecast Section */}
      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8">
          <Card className="bg-gradient-to-br from-purple-500/10 to-primary/5 border-purple-500/20">
            <CardHeader className="flex flex-row items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-primary flex items-center justify-center">
                  <Brain className="w-5 h-5 text-white" />
                </div>
                <div>
                  <CardTitle className="text-white flex items-center gap-2">
                    AI Cash Flow Forecast
                    <Badge variant="outline" className="border-purple-500/50 text-purple-400 text-xs">
                      <Sparkles className="w-3 h-3 mr-1" />
                      GPT-5.1
                    </Badge>
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">3-month predictive analysis</p>
                </div>
              </div>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={fetchCashFlowForecast}
                disabled={forecastLoading}
              >
                <RefreshCw className={`w-4 h-4 ${forecastLoading ? 'animate-spin' : ''}`} />
              </Button>
            </CardHeader>
            <CardContent>
              {forecastLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
                    <span className="text-sm text-muted-foreground">AI is analyzing your financial patterns...</span>
                  </div>
                </div>
              ) : forecast && forecast.forecast && forecast.forecast.length > 0 ? (
                <div className="space-y-6">
                  {/* Forecast Chart */}
                  <div className="h-[250px] min-h-[250px]">
                    <ResponsiveContainer width="100%" height={250}>
                      <LineChart data={forecast.forecast}>
                        <defs>
                          <linearGradient id="colorBalance" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#A855F7" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#A855F7" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                        <XAxis dataKey="month" stroke="#64748b" fontSize={12} />
                        <YAxis stroke="#64748b" fontSize={12} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: '#161822', 
                            border: '1px solid rgba(168, 85, 247, 0.3)',
                            borderRadius: '8px'
                          }}
                          formatter={(value, name) => [formatCurrency(value), name === 'predicted_balance' ? 'Predicted Balance' : name === 'predicted_income' ? 'Predicted Income' : 'Predicted Expenses']}
                        />
                        <Legend />
                        <Line type="monotone" dataKey="predicted_balance" stroke="#A855F7" strokeWidth={2} dot={{ fill: '#A855F7' }} name="Balance" />
                        <Line type="monotone" dataKey="predicted_income" stroke="#10B981" strokeWidth={2} dot={{ fill: '#10B981' }} name="Income" />
                        <Line type="monotone" dataKey="predicted_expenses" stroke="#EF4444" strokeWidth={2} dot={{ fill: '#EF4444' }} name="Expenses" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Forecast Summary Cards */}
                  <div className="grid grid-cols-3 gap-4">
                    {forecast.forecast.map((item, index) => (
                      <div key={item.month} className="bg-orbit-bg/50 rounded-xl p-4 text-center">
                        <p className="text-xs text-muted-foreground mb-1">{item.month}</p>
                        <p className="text-lg font-bold text-white">{formatCurrency(item.predicted_balance)}</p>
                        <div className="flex items-center justify-center gap-1 mt-1">
                          <span className="text-xs text-muted-foreground">Confidence:</span>
                          <span className={`text-xs font-medium ${item.confidence > 0.7 ? 'text-emerald-400' : item.confidence > 0.5 ? 'text-yellow-400' : 'text-red-400'}`}>
                            {Math.round((item.confidence || 0.8) * 100)}%
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <LineChartIcon className="w-12 h-12 text-purple-400/50 mb-4" />
                  <p className="text-muted-foreground">Add more financial data to generate AI forecasts</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Forecast Insights & Risk */}
        <div className="lg:col-span-4 space-y-6">
          {/* Risk Level */}
          {forecast && (
            <Card className="bg-orbit-card border-orbit-border">
              <CardHeader className="pb-3">
                <CardTitle className="text-white text-base">Cash Flow Risk</CardTitle>
              </CardHeader>
              <CardContent>
                <div className={`flex items-center gap-3 p-4 rounded-xl ${
                  forecast.risk_level === 'low' ? 'bg-emerald-500/10' :
                  forecast.risk_level === 'medium' ? 'bg-yellow-500/10' : 'bg-red-500/10'
                }`}>
                  {forecast.risk_level === 'low' ? (
                    <CheckCircle className="w-8 h-8 text-emerald-400" />
                  ) : forecast.risk_level === 'medium' ? (
                    <Info className="w-8 h-8 text-yellow-400" />
                  ) : (
                    <AlertTriangle className="w-8 h-8 text-red-400" />
                  )}
                  <div>
                    <p className={`font-semibold capitalize ${
                      forecast.risk_level === 'low' ? 'text-emerald-400' :
                      forecast.risk_level === 'medium' ? 'text-yellow-400' : 'text-red-400'
                    }`}>
                      {forecast.risk_level || 'Medium'} Risk
                    </p>
                    <p className="text-xs text-muted-foreground">Based on AI analysis</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* AI Recommendation */}
          {forecast && forecast.recommendation && (
            <Card className="bg-orbit-card border-orbit-border">
              <CardHeader className="pb-3">
                <CardTitle className="text-white text-base flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-purple-400" />
                  AI Recommendation
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">{forecast.recommendation}</p>
              </CardContent>
            </Card>
          )}

          {/* Forecast Insights */}
          {forecast && forecast.insights && forecast.insights.length > 0 && (
            <Card className="bg-orbit-card border-orbit-border">
              <CardHeader className="pb-3">
                <CardTitle className="text-white text-base">Forecast Insights</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {forecast.insights.slice(0, 3).map((insight, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm">
                      <div className="w-1.5 h-1.5 rounded-full bg-purple-400 mt-2 flex-shrink-0" />
                      <span className="text-muted-foreground">{insight}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
